import { useEffect, useMemo, useRef } from "react"

import {
  adminGetOperatorChatQuery,
  AdminOperatorChatDetail,
  AdminOperatorMessage,
  AdminOperatorRun
} from "@domain/ai_operator"
import useTranslate from "@hooks/use_translate"
import LinkIcon from "@mui/icons-material/Link"
import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { showSuccessToast } from "@utils/toast"

import { Message } from "./OperatorChatThread"
import RunStatus from "./RunStatus"
import { formatMs, formatUsd, runTone } from "./operatorRun.utils"

type Props = {
  chatId: string
  activeRunId?: string
  onOpenRun: (runId: string) => void
}

type Item =
  | { at: string; message: AdminOperatorMessage; run?: AdminOperatorRun }
  | { at: string; run: AdminOperatorRun; message?: undefined }

function RunChip({
  run,
  active,
  onOpen
}: {
  run: AdminOperatorRun
  active: boolean
  onOpen: () => void
}) {
  const tone = runTone(run)
  return (
    <ButtonBase
      onClick={onOpen}
      sx={{
        px: 1,
        py: 0.25,
        gap: 1,
        borderRadius: 1,
        border: 1,
        borderColor: active ? "primary.main" : `${tone}.light`,
        bgcolor: active ? "action.selected" : "transparent",
        "&:hover": { bgcolor: "action.hover" }
      }}
    >
      <RunStatus run={run} />
      <Typography variant="caption" color="text.secondary" noWrap>
        {[
          formatMs(run.durationMs),
          formatUsd(run.costUsd),
          run.tools.length ? `${run.tools.length} tool` : ""
        ]
          .filter(Boolean)
          .join(" · ")}
      </Typography>
    </ButtonBase>
  )
}

/** Messages in order; a run that wrote no message (failed, still running) sits where it started. */
function timeline(detail: AdminOperatorChatDetail): Item[] {
  const runs = new Map(detail.runs.map((run) => [run.id, run]))
  const linked = new Set(detail.messages.map((message) => message.runId))
  const items: Item[] = [
    ...detail.messages.map((message) => ({
      at: message.createdAt,
      message,
      run: message.runId ? runs.get(message.runId) : undefined
    })),
    ...detail.runs
      .filter((run) => !linked.has(run.id))
      .map((run) => ({ at: run.createdAt, run }))
  ]
  return items.sort((a, b) => a.at.localeCompare(b.at))
}

export default function OperatorChatView({
  chatId,
  activeRunId,
  onOpenRun
}: Props) {
  const { translate } = useTranslate()
  const detail = useQuery({
    queryKey: ["ai-operator-chat", chatId],
    queryFn: async () => {
      const response = await adminGetOperatorChatQuery(chatId)
      if (!response.isSuccess || !response.data) {
        throw new Error(
          response.errorMessage || translate("ai_operator_chat_empty")
        )
      }
      return response.data
    },
    retry: false
  })
  const items = useMemo(
    () => (detail.data ? timeline(detail.data) : []),
    [detail.data]
  )
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const box = scrollRef.current
    if (box) box.scrollTop = box.scrollHeight
  }, [items.length])

  if (detail.isLoading) {
    return (
      <Stack alignItems="center" p={4} flex={1}>
        <CircularProgress size={24} />
      </Stack>
    )
  }
  if (detail.error || !detail.data) {
    return (
      <Box p={3} flex={1}>
        <Alert severity="error">{detail.error?.message}</Alert>
      </Box>
    )
  }

  const { chat, runs } = detail.data
  const head = runs[runs.length - 1]
  const failed = runs.filter((run) => run.status === "FAILED").length
  const cost = runs.reduce((sum, run) => sum + run.costUsd, 0)

  return (
    <Stack flex={1} minWidth={0} height="100%">
      <Stack direction="row" alignItems="center" gap={1} px={3} py={1.5}>
        <Stack flex={1} minWidth={0}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {chat?.title || head?.request}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {[
              head?.userName ?? head?.cPersonId,
              head?.customerName ?? head?.customerId,
              `${runs.length} ${translate("ai_operator_health_runs")}`,
              failed
                ? `${failed} ${translate("ai_operator_health_failed")}`
                : "",
              formatUsd(cost)
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Stack>
        <Tooltip title={translate("ai_operator_copy_link")}>
          <IconButton
            size="small"
            onClick={() =>
              void navigator.clipboard
                .writeText(
                  `${window.location.origin}/ai/operator?chat=${chatId}`
                )
                .then(() => showSuccessToast(translate("ai_operator_copied")))
            }
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
      <Stack
        ref={scrollRef}
        gap={1.5}
        px={3}
        py={2}
        sx={{ overflow: "auto", flex: 1 }}
      >
        {items.map((item) => (
          <Stack
            key={item.message?.id ?? item.run?.id}
            gap={0.5}
            alignItems={
              item.message?.role === "user" ? "flex-end" : "flex-start"
            }
          >
            {item.message ? <Message message={item.message} /> : null}
            {item.run ? (
              <Stack gap={0.25} alignItems="flex-start">
                {item.message ? null : (
                  <Typography variant="caption" color="text.secondary">
                    {item.run.request}
                  </Typography>
                )}
                <RunChip
                  run={item.run}
                  active={item.run.id === activeRunId}
                  onOpen={() => item.run && onOpenRun(item.run.id)}
                />
                {item.run.error ? (
                  <Typography variant="caption" color="error">
                    {`${item.run.error.code} · ${item.run.error.message}`}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}
